import { useCallback, useEffect, useState } from "react";
import { UploadCloud, FileText, Download, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, downloadDocument } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const STATUS_COLORS = {
  RECEIVED: "bg-teal-light text-teal",
  "UNDER REVIEW": "bg-champagne-light text-champagne-dark",
  ACCEPTED: "bg-sage-light text-sage",
  "REPLACEMENT NEEDED": "bg-red-950/60 text-red-300",
  "TRANSLATION NEEDED": "bg-champagne-light text-champagne-dark",
  "APOSTILLE NEEDED": "bg-champagne-light text-champagne-dark",
  EXPIRED: "bg-red-950/60 text-red-300",
  "MISSING PAGE": "bg-red-950/60 text-red-300",
  SUBMITTED: "bg-teal-light text-teal",
  REQUESTED: "bg-sand text-mutedink",
  "NEEDS REPLACEMENT": "bg-red-950/60 text-red-300",
};

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [requests, setRequests] = useState([]);
  const [cases, setCases] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [meta, setMeta] = useState({ name: "", category: "", case_id: "", description: "", doc_date: "", expiry_date: "" });
  const [uploadTarget, setUploadTarget] = useState(null);

  const load = useCallback(() => {
    api.get("/documents").then((r) => setDocs(r.data)).catch(() => {});
    api.get("/doc-requests").then((r) => setRequests(r.data)).catch(() => {});
    api.get("/cases/mine").then((r) => setCases(r.data)).catch(() => {});
    api.get("/meta/options").then((r) => setCategories(r.data.doc_categories)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      setPendingFile(file);
      setMeta((m) => ({ ...m, name: m.name || file.name.replace(/\.[^.]+$/, "") }));
    }
  };

  const doUpload = async () => {
    if (!pendingFile || !meta.name || !meta.category) {
      toast.error("Please choose a file, name, and category.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", pendingFile);
      fd.append("name", meta.name);
      fd.append("category", meta.category);
      fd.append("case_id", meta.case_id || "");
      fd.append("description", meta.description || "");
      fd.append("doc_date", meta.doc_date || "");
      fd.append("expiry_date", meta.expiry_date || "");
      if (uploadTarget) {
        fd.append("doc_request_id", uploadTarget.request_id);
        fd.append("item_id", uploadTarget.item_id);
      }
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded securely.");
      setPendingFile(null);
      setUploadTarget(null);
      setMeta({ name: "", category: "", case_id: "", description: "", doc_date: "", expiry_date: "" });
      load();
    } catch (err) {
      console.error("Document upload failed", err);
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : err.response
            ? `Upload failed (error ${err.response.status}). Your file is still selected — please try again.`
            : "No connection — your file is still selected. Check your internet and tap Upload again."
      );
    } finally {
      setUploading(false);
    }
  };

  const remove = async (doc) => {
    try {
      await api.delete(`/documents/${doc.doc_id}`);
      toast.success("Document removed.");
      load();
    } catch (err) {
      console.error("Document delete failed", err);
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : err.response ? `Could not remove the document (error ${err.response.status}).` : "No connection — check your internet and try again.");
    }
  };

  const startRequestUpload = (req, item) => {
    setUploadTarget({ request_id: req.request_id, item_id: item.item_id });
    setMeta((m) => ({ ...m, name: item.name, category: "Other" }));
    document.getElementById("upload-panel")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="space-y-8" data-testid="portal-documents-page">
      <div>
        <h1 className="text-2xl font-serif font-semibold text-navy tracking-tight">Documents</h1>
        <p className="mt-1 text-sm text-mutedink">Your secure document vault. Everything you upload is encrypted and visible only to you and your Full Circle team.</p>
      </div>

      {/* Requested documents checklist */}
      {requests.some((r) => r.items.some((i) => i.status !== "ACCEPTED")) && (
        <section className="bg-champagne-light border border-champagne/50 rounded-2xl p-6" data-testid="doc-requests-section">
          <h2 className="text-base font-sans font-semibold text-navy mb-4">Documents we need from you</h2>
          <div className="space-y-5">
            {requests.map((req) => (
              <div key={req.request_id}>
                <ul className="space-y-2">
                  {req.items.map((item) => (
                    <li key={item.item_id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-surface rounded-xl px-4 py-3 border border-line" data-testid={`doc-request-item-${item.item_id}`}>
                      <div className="flex items-center gap-3">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_COLORS[item.status] || "bg-sand text-mutedink"}`}>{item.status}</span>
                        <span className="text-sm text-ink">{item.name}</span>
                      </div>
                      {(item.status === "REQUESTED" || item.status === "NEEDS REPLACEMENT") && (
                        <label className="cursor-pointer text-xs font-semibold text-teal hover:text-teal-dark" data-testid={`doc-request-upload-${item.item_id}`}>
                          Upload this
                          <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.heic" onChange={(e) => { const f = e.target.files?.[0]; if (f) { setPendingFile(f); startRequestUpload(req, item); } }} />
                        </label>
                      )}
                      {item.note && <p className="text-xs text-red-600 sm:basis-full">{item.note}</p>}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Upload */}
      <section id="upload-panel" className="bg-surface border border-line rounded-2xl p-6 shadow-sm" data-testid="upload-section">
        <h2 className="text-base font-sans font-semibold text-navy mb-4">Upload a document {uploadTarget && <span className="text-xs text-teal font-normal">— for requested item</span>}</h2>
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`rounded-xl border-2 border-dashed p-8 text-center transition-colors ${dragging ? "border-teal bg-teal-light/50" : "border-line bg-ivory"}`}
          data-testid="upload-dropzone"
        >
          <UploadCloud className="h-8 w-8 text-teal mx-auto mb-3" />
          {pendingFile ? (
            <p className="text-sm font-medium text-navy">{pendingFile.name}</p>
          ) : (
            <>
              <p className="text-sm text-mutedink">Drag a file here, or</p>
              <label className="mt-2 inline-block cursor-pointer text-sm font-semibold text-teal hover:text-teal-dark" data-testid="upload-browse-button">
                browse from your device
                <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.heic" onChange={(e) => { const f = e.target.files?.[0]; if (f) { setPendingFile(f); setMeta((m) => ({ ...m, name: m.name || f.name.replace(/\.[^.]+$/, "") })); } }} />
              </label>
              <p className="mt-2 text-xs text-mutedink">PDF, JPG, PNG, DOC, DOCX, HEIC — up to 15MB. On mobile, you can photograph a document directly.</p>
            </>
          )}
        </div>

        {pendingFile && (
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4 animate-fade-up" data-testid="upload-meta-form">
            <div>
              <Label htmlFor="doc-name">Document name *</Label>
              <Input id="doc-name" data-testid="doc-name-input" value={meta.name} onChange={(e) => setMeta({ ...meta, name: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label>Category *</Label>
              <Select value={meta.category} onValueChange={(v) => setMeta({ ...meta, category: v })}>
                <SelectTrigger className="mt-1.5" data-testid="doc-category-select"><SelectValue placeholder="Choose a category" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Related case (optional)</Label>
              <Select value={meta.case_id} onValueChange={(v) => setMeta({ ...meta, case_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1.5" data-testid="doc-case-select"><SelectValue placeholder="Not case-specific" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not case-specific</SelectItem>
                  {cases.map((c) => <SelectItem key={c.case_id} value={c.case_id}>{c.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="doc-desc">Description (optional)</Label>
              <Input id="doc-desc" data-testid="doc-description-input" value={meta.description} onChange={(e) => setMeta({ ...meta, description: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="doc-date">Date of document</Label>
              <Input id="doc-date" type="date" data-testid="doc-date-input" value={meta.doc_date} onChange={(e) => setMeta({ ...meta, doc_date: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="doc-expiry">Expiration date (if applicable)</Label>
              <Input id="doc-expiry" type="date" data-testid="doc-expiry-input" value={meta.expiry_date} onChange={(e) => setMeta({ ...meta, expiry_date: e.target.value })} className="mt-1.5" />
            </div>
            <div className="sm:col-span-2 flex gap-3">
              <Button onClick={doUpload} disabled={uploading} className="bg-teal text-white hover:bg-teal-dark hover:text-white rounded-full" data-testid="doc-upload-submit">
                {uploading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading...</> : "Upload Securely"}
              </Button>
              <Button variant="ghost" onClick={() => { setPendingFile(null); setUploadTarget(null); }} className="text-mutedink" data-testid="doc-upload-cancel">Cancel</Button>
            </div>
          </div>
        )}
      </section>

      {/* Document list */}
      <section data-testid="documents-list">
        <h2 className="text-base font-sans font-semibold text-navy mb-4">All documents</h2>
        {docs.length === 0 ? (
          <p className="text-sm text-mutedink bg-surface border border-dashed border-line rounded-2xl p-8 text-center">No documents yet. Uploads from you and files shared by your Full Circle team will appear here.</p>
        ) : (
          <div className="space-y-3">
            {docs.map((d) => (
              <div key={d.doc_id} className="bg-surface border border-line rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`document-row-${d.doc_id}`}>
                <FileText className="h-5 w-5 text-teal flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-navy truncate">{d.name}</p>
                  <p className="text-xs text-mutedink">
                    {d.category} · {new Date(d.created_at).toLocaleDateString()}
                    {d.uploaded_by_role !== "client" && " · Shared by CF Full Circle"}
                    {d.designation && ` · ${d.designation}`}
                  </p>
                  {d.review_note && <p className="text-xs text-red-600 mt-1">{d.review_note}</p>}
                </div>
                <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full w-fit ${STATUS_COLORS[d.status] || "bg-sand text-mutedink"}`} data-testid={`document-status-${d.doc_id}`}>{d.status}</span>
                <div className="flex gap-1">
                  <button onClick={() => downloadDocument(d.doc_id, d.original_filename)} className="p-2 text-teal hover:text-teal-dark transition-colors" aria-label={`Download ${d.name}`} data-testid={`document-download-${d.doc_id}`}>
                    <Download className="h-4 w-4" />
                  </button>
                  {d.uploaded_by_role === "client" && (
                    <button onClick={() => remove(d)} className="p-2 text-mutedink hover:text-red-600 transition-colors" aria-label={`Delete ${d.name}`} data-testid={`document-delete-${d.doc_id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
